import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { sameId } from '../../../shared/api/wire'
import type { CommandFeedback } from '../../../shared/model/feedback'
import { newUuid } from '../../../shared/model/ids'
import { dropResource } from '../../../shared/model/keys'
import { PAY_CHECKING_LINK_TEXT, PAY_SAVING_CART_TEXT } from './healthTexts'
import { PAY_WAITING_RECEIPT_TEXT } from './fastTexts'
import { newSaleStarted, saleSeatCleared, seatKey, type SeatRef } from './saleSlice'
import type { PaymentKind } from './types'

/**
 * Попытка оплаты (RP-PAY-ONE): одно нажатие «Картой» / «Наличными» / «Повторить оплату» — одно окно до итога. Здесь — только нажатия
 * кассира, а не ответы служб: «отменил кассир», «закрыто», «свёрнуто», «ручная сверка открыта». Итог отмены, запоздавшее чтение продажи и
 * живое событие приходят в любом порядке, а нажатия — нет; поэтому отказ, тихую отмену и закрытие решают они (этап — `paymentStageOf`).
 */
export interface PaymentAttempt {
  /** UUID нажатия; попытка, подхваченная по идущей продаже (F5, другая вкладка), — `sale:<id продажи>`. Ключ монтирования окна. */
  id: string
  resourceId: string
  registerKey: string
  /** Продажа попытки; `null` — нажали «Картой» сразу после первой позиции, продажа ещё создаётся: привяжется к ответу (`attemptOf`). */
  saleId: string | null
  kind: PaymentKind
  /** Когда нажали (ISO): счётчик «идёт m:ss», пока ничего точнее нет. */
  startedAt: string
  /** Кассир нажал «Отменить»: отказ этой попытки — тихий, окна «Не оплачено» нет. */
  cancelledByCashier: boolean
  /** Кассир закрыл окно («Закрыть»): до следующего нажатия оплаты окна нет. */
  closed: boolean
  /** «Свернуть»: окно не показывается, в блоке состояния — «Ход оплаты: …»; само не разворачивается. */
  collapsed: boolean
  /** Ручная сверка открыта в окне. */
  resolving: boolean
  /**
   * «Отменить» нажато, а связи нет: отмена оплаты поставлена в очередь и уйдёт тем же ключом, как только связь появится (RP-SALE-FAST,
   * шаг 6). Только в памяти вкладки: после F5 исход читается продажей.
   */
  cancelQueued?: boolean
  /**
   * Попытку видели идущей в этой вкладке (нажатие, чтение хода, явное «открыть»): только тогда её отказ и поздний слип объявляются окном.
   * После F5 — `false`: отказ прошлой попытки — строкой в блоке состояния, окно не навязывается (вопрос 1 карточки).
   */
  live: boolean
}

export interface PaymentAttemptsState {
  /** Ключ — `<rid>|<registerKey>`: одна попытка на кассу. Срок — `session`. */
  bySeat: Record<string, PaymentAttempt>
  /**
   * Ход и отказы команд цикла оплаты (`sale.pay*`, `sale.cancel-payment`, `sale.retry`…) — свои у попытки и только в памяти вкладки:
   * общий итог команд кассы оплатой не перетирается, а внизу уведомлений оплаты нет.
   */
  feedback: Record<string, CommandFeedback>
}

const initialState: PaymentAttemptsState = { bySeat: {}, feedback: {} }

interface AttemptRef extends SeatRef {
  saleId: string
  kind: PaymentKind
}

/** Нажатие оплаты: продажа может ещё не существовать (`null`), `saving` — корзина не сохранена, оплата ждёт её сохранения. */
interface StartedRef extends SeatRef {
  saleId: string | null
  kind: PaymentKind
  saving?: boolean
  /** Чек предыдущей продажи ещё печатается (быстрая оплата): оплата ждёт его конца. */
  waitingReceipt?: boolean
}

/** Команда цикла оплаты «ждём сохранения корзины» — этап окна `saving`. */
export const SAVE_COMMAND = 'sale.pay-save'

/** Команда цикла оплаты «ждём конца печати чека предыдущей продажи» (быстрая оплата) — этап окна `saving`. */
export const WAIT_RECEIPT_COMMAND = 'sale.pay-wait-receipt'

/** Одна и та же продажа; две «ещё не созданных» — тоже одна. */
const sameSale = (left: string | null, right: string | null): boolean =>
  left === null || right === null ? left === right : sameId(left, right)

/** Команды подготовки оплаты: пока одна из них идёт, второе нажатие оплаты попытку не заменяет. */
export const PREPARE_COMMANDS: ReadonlySet<string> = new Set(['sale.pay-card', 'sale.pay-cash', 'sale.pay', 'sale.payment-kind', SAVE_COMMAND, WAIT_RECEIPT_COMMAND])

const adoptedId = (saleId: string) => `sale:${saleId.toLowerCase()}`

/** Попытка этой продажи: есть — она; нет (F5 без записи, другая вкладка) — подхваченная, ключ по продаже. */
function attemptOf(state: PaymentAttemptsState, ref: AttemptRef): PaymentAttempt {
  const key = seatKey(ref)
  const current = state.bySeat[key]
  if (current !== undefined && current.saleId === null) {
    // Продажа создана: попытка, нажатая до её создания, становится попыткой этой продажи.
    current.saleId = ref.saleId
    return current
  }
  if (current !== undefined && sameSale(current.saleId, ref.saleId)) return current
  const adopted: PaymentAttempt = {
    id: adoptedId(ref.saleId),
    resourceId: ref.resourceId,
    registerKey: ref.registerKey,
    saleId: ref.saleId,
    kind: ref.kind,
    startedAt: new Date(0).toISOString(),
    cancelledByCashier: false,
    closed: false,
    collapsed: false,
    resolving: false,
    live: true,
  }
  state.bySeat[key] = adopted
  delete state.feedback[key]
  return adopted
}

const paymentAttemptsSlice = createSlice({
  name: 'aqsi/paymentAttempts',
  initialState,
  reducers: {
    /** Нажата оплата: новая попытка и сразу «Проверяем связь с кассой…» — окно открывается по нажатию, а не после проверки. */
    paymentAttemptStarted: {
      reducer(state, action: PayloadAction<StartedRef & { id: string; startedAt: string }>) {
        const key = seatKey(action.payload)
        const current = state.bySeat[key]
        const preparing = state.feedback[key]
        // Двойное нажатие, пока подготовка идёт: та же попытка — окно не монтируется заново.
        if (current !== undefined && !current.closed && sameSale(current.saleId, action.payload.saleId) && preparing?.phase === 'sending' && PREPARE_COMMANDS.has(preparing.command)) return
        const { id, startedAt, resourceId, registerKey, saleId, kind, saving, waitingReceipt } = action.payload
        state.bySeat[key] = { id, resourceId, registerKey, saleId, kind, startedAt, cancelledByCashier: false, closed: false, collapsed: false, resolving: false, live: true }
        state.feedback[key] = {
          command: waitingReceipt ? WAIT_RECEIPT_COMMAND : saving ? SAVE_COMMAND : kind === 'Card' ? 'sale.pay-card' : 'sale.pay-cash',
          target: registerKey,
          phase: 'sending',
          error: null,
          note: waitingReceipt ? PAY_WAITING_RECEIPT_TEXT : saving ? PAY_SAVING_CART_TEXT : PAY_CHECKING_LINK_TEXT,
        }
      },
      prepare(ref: StartedRef) {
        return { payload: { ...ref, id: newUuid(), startedAt: new Date().toISOString() } }
      },
    },
    /** Ход или итог команды цикла оплаты — в окно попытки. `saleId` нет и попытки нет — сказать некому, итог не пишется. */
    paymentFeedbackSet(state, action: PayloadAction<SeatRef & { saleId: string | null; kind?: PaymentKind; feedback: CommandFeedback }>) {
      const { saleId, kind, feedback, ...ref } = action.payload
      const key = seatKey(ref)
      if (saleId === null) {
        if (state.bySeat[key] !== undefined) state.feedback[key] = feedback
        return
      }
      attemptOf(state, { ...ref, saleId, kind: kind ?? state.bySeat[key]?.kind ?? 'Card' })
      state.feedback[key] = feedback
    },
    /** Отмена оплаты ждёт связи (`true`) или ушла/снята (`false`). */
    paymentCancelQueued(state, action: PayloadAction<AttemptRef & { queued: boolean }>) {
      attemptOf(state, action.payload).cancelQueued = action.payload.queued
    },
    /** «Отменить» нажато: отказ этой попытки — тихий. */
    paymentCancelPressed(state, action: PayloadAction<AttemptRef>) {
      attemptOf(state, action.payload).cancelledByCashier = true
    },
    paymentCollapsed(state, action: PayloadAction<AttemptRef>) {
      attemptOf(state, action.payload).collapsed = true
    },
    /** Развернуть: «Ход оплаты: …» или строка позднего слипа — кассир сам открыл окно, значит, оно снова говорит. */
    paymentExpanded(state, action: PayloadAction<AttemptRef>) {
      const attempt = attemptOf(state, action.payload)
      attempt.collapsed = false
      attempt.closed = false
      attempt.cancelledByCashier = false
      attempt.live = true
    },
    paymentClosed(state, action: PayloadAction<SeatRef>) {
      const key = seatKey(action.payload)
      const attempt = state.bySeat[key]
      if (attempt === undefined) return
      attempt.closed = true
      attempt.collapsed = false
      attempt.resolving = false
      delete state.feedback[key]
    },
    paymentResolveOpened(state, action: PayloadAction<AttemptRef>) {
      attemptOf(state, action.payload).resolving = true
    },
    paymentResolveClosed(state, action: PayloadAction<SeatRef>) {
      const attempt = state.bySeat[seatKey(action.payload)]
      if (attempt !== undefined) attempt.resolving = false
    },
    /** Чтение показало идущую оплату или чек: попытка живая — её отказ объявит окно (и после F5). */
    paymentAttemptLive(state, action: PayloadAction<AttemptRef>) {
      attemptOf(state, action.payload).live = true
    },
    paymentAttemptsRestored(state, action: PayloadAction<Record<string, PaymentAttempt>>) {
      state.bySeat = action.payload
      state.feedback = {}
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      dropResource(state.bySeat, action.payload.resourceId)
      dropResource(state.feedback, action.payload.resourceId)
    })
    // Новая продажа и новая смена — новой попытке нечего наследовать.
    builder.addCase(newSaleStarted, (state, action) => {
      delete state.bySeat[seatKey(action.payload)]
      delete state.feedback[seatKey(action.payload)]
    })
    builder.addCase(saleSeatCleared, (state, action) => {
      delete state.bySeat[seatKey(action.payload)]
      delete state.feedback[seatKey(action.payload)]
    })
  },
})

export const {
  paymentAttemptStarted,
  paymentFeedbackSet,
  paymentCancelPressed,
  paymentCancelQueued,
  paymentCollapsed,
  paymentExpanded,
  paymentClosed,
  paymentResolveOpened,
  paymentResolveClosed,
  paymentAttemptLive,
  paymentAttemptsRestored,
} = paymentAttemptsSlice.actions
export const paymentAttemptsReducer = paymentAttemptsSlice.reducer

export function paymentAttemptOf(state: PaymentAttemptsState, ref: SeatRef): PaymentAttempt | undefined {
  return state.bySeat[seatKey(ref)]
}

export function paymentFeedbackOf(state: PaymentAttemptsState, ref: SeatRef): CommandFeedback | undefined {
  return state.feedback[seatKey(ref)]
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ATTEMPT_ID = /^(sale:)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Проверка сохранённого (D-072). Восстановленная попытка — не живая (`live: false`): вкладка её не видела идущей, поэтому её прошлый
 * отказ окном не объявляется; идущая оплата (чтение хода) делает её живой снова.
 */
export function decodePaymentAttempts(raw: unknown): Record<string, PaymentAttempt> | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const result: Record<string, PaymentAttempt> = {}
  for (const value of Object.values(raw as Record<string, unknown>).slice(0, 50)) {
    if (typeof value !== 'object' || value === null) continue
    const entry = value as Record<string, unknown>
    if (
      typeof entry.id !== 'string' ||
      !ATTEMPT_ID.test(entry.id) ||
      typeof entry.resourceId !== 'string' ||
      !UUID.test(entry.resourceId) ||
      typeof entry.registerKey !== 'string' ||
      !UUID.test(entry.registerKey) ||
      typeof entry.saleId !== 'string' ||
      !UUID.test(entry.saleId) ||
      (entry.kind !== 'Card' && entry.kind !== 'Cash') ||
      typeof entry.startedAt !== 'string' ||
      Number.isNaN(Date.parse(entry.startedAt)) ||
      typeof entry.cancelledByCashier !== 'boolean' ||
      typeof entry.closed !== 'boolean' ||
      typeof entry.collapsed !== 'boolean' ||
      typeof entry.resolving !== 'boolean'
    ) {
      continue
    }
    const attempt: PaymentAttempt = {
      id: entry.id,
      resourceId: entry.resourceId,
      registerKey: entry.registerKey,
      saleId: entry.saleId,
      kind: entry.kind,
      startedAt: entry.startedAt,
      cancelledByCashier: entry.cancelledByCashier,
      closed: entry.closed,
      collapsed: entry.collapsed,
      resolving: entry.resolving,
      live: false,
    }
    result[seatKey(attempt)] = attempt
  }
  return result
}
